<template>
  <div class="w-full h-full p-1 pt-0 bg-gray-200/20 rounded-xl">
    <div class="mx-4 pt-3.5 pb-[13px] border-b border-gray-200/50">
      <div class="flex items-center gap-x-2.5">
        <component :is="stemIcon" class="size-[22px] text-gray-800" />

        <div class="font-medium text-[15px] leading-[20px] text-gray-800">
          {{ props.name }}
        </div>
      </div>
    </div>

    <div class="px-4 pt-4 pb-4">
      <div class="text-[12px] text-gray-500 leading-4 mb-[5px]">
        Isolated stem
      </div>

      <div class="flex items-center gap-x-5">
        <div
          class="min-w-9 h-9 bg-white border border-gray-200 rounded-full flex items-center justify-center cursor-pointer"
          @click="handleClick"
        >
          <component :is="playIcon" class="size-[18px]" :style="iconStyles" />
        </div>

        <div ref="el" class="w-full"></div>
      </div>
    </div>

    <div v-if="props.sheetMusic" class="bg-white rounded-lg px-4 pt-2.5 pb-2.5">
      <div class="text-[12px] text-gray-500 leading-4">Stem sheet music</div>

      <div class="flex items-center gap-x-5">
        <div class="relative min-w-0 flex-1 overflow-hidden">
          <div ref="sheetMusicRef" class="w-full"></div>
          <div
            class="absolute top-0 right-0 h-full w-32 bg-gradient-to-r from-transparent to-white pointer-events-none"
          ></div>
        </div>

        <Button
          variant="stroke"
          :size="isMobile ? 'lg' : 'md'"
          :icon="FileTextIcon"
          class="shrink-0"
          @click="handleViewMore"
        >
          View
        </Button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Button } from '@dacr/library'
import { PlayIcon, PauseIcon, FileTextIcon } from '@dacr/icons'
import { stemIcon as resolveStemIcon } from '@/features/copyright/utils/stem'
import {
  OpenSheetMusicDisplay,
  FillEmptyMeasuresWithWholeRests,
} from 'opensheetmusicdisplay'
import { fetchMusicXmlDocument } from '@/features/copyright/utils/musicxml'
import { useDevice } from '@/shared/composables/useDevice'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps<{
  name: string
  url: string
  sheetMusic?: string
  trackIndex: number
}>()

const route = useRoute()
const router = useRouter()

const { isMobile } = useDevice()

const el = ref<HTMLElement>()
const wavesurfer = ref<InstanceType<typeof import('wavesurfer.js').default>>()
const sheetMusicRef = ref<HTMLElement>()

const isPlaying = ref(false)
const sheetMusicPreviewZoom = 0.7

const playIcon = computed(() => (isPlaying.value ? PauseIcon : PlayIcon))

const iconStyles = computed(() => ({
  marginLeft: isPlaying.value ? null : '3px',
}))

const stemIcon = computed(() => resolveStemIcon(props.name))

const handleClick = () => {
  wavesurfer.value?.playPause()
  isPlaying.value = !!wavesurfer.value?.isPlaying()
}

const handleViewMore = () => {
  const base = `/file/${route.params.id}`
  router.push(
    `${base}/tracks/${props.trackIndex}/${props.name.toLowerCase()}/sheet-music`
  )
}

onMounted(async () => {
  if (!el.value) return

  const WaveSurfer = (await import('wavesurfer.js')).default

  wavesurfer.value = WaveSurfer.create({
    container: el.value,
    waveColor: '#AFB5C0',
    progressColor: '#222',
    url: props.url,
    height: 40,
    cursorWidth: 0,
  })

  if (props.sheetMusic && sheetMusicRef.value) {
    try {
      const musicXml = await fetchMusicXmlDocument(props.sheetMusic)

      const musicSheet = new OpenSheetMusicDisplay(sheetMusicRef.value, {
        drawTitle: false,
        drawPartNames: false,
        drawCredits: false,
        drawingParameters: 'compacttight',
        drawMetronomeMarks: false,
        renderSingleHorizontalStaffline: true,
        autoResize: false,
        autoGenerateMultipleRestMeasuresFromRestMeasures: false,
        fillEmptyMeasuresWithWholeRest:
          FillEmptyMeasuresWithWholeRests.YesVisible,
      })
      musicSheet.EngravingRules.RenderMultipleRestMeasures = false
      await musicSheet.load(musicXml)
      musicSheet.Zoom = sheetMusicPreviewZoom
      musicSheet.render()
    } catch {
      sheetMusicRef.value.replaceChildren()
    }
  }
})
</script>
